import {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  fetchActiveSessionCounts,
  fetchResourceChallengeStatus,
  fetchChallengeSubmissions,
  clearChallengeSubmissions,
  selectActiveSessionStatus,
  selectActiveSessionStatusLoading,
  selectResourceChallengeStatus,
  selectChallengeSubmissions,
} from '../slices/marketSlice';
import {selectSelectedResource} from '../slices/preferencesSlice';
import {
  formatDateTimeInResourceTz,
  formatUtcDateTime,
  formatTargetDay,
  getResourceTimezoneLabel,
} from '../utils/datetime';

const GATE_REFRESH_INTERVAL_MS = 60 * 1000;

// Re-exported for any legacy importers; new code should import from utils/datetime.
export const formatBrusselsDateTime = formatDateTimeInResourceTz;
export {formatUtcDateTime};

const formatChallengeBadge = (challenge) => challenge?.horizonLabel || `D+${challenge?.horizonOffset ?? 0}`;

/**
 * SessionStatusChip component displays the active session summary.
 * It renders a compact inline status pill with session status on the left
 * and the forecasting period stacked on the right when available.
 */
export default function SessionStatusChip() {
  const dispatch = useDispatch();
  const [expandedChipId, setExpandedChipId] = useState(null);
  const toggleChip = (id) => setExpandedChipId(prev => prev === id ? null : id);

  useEffect(() => {
    if (expandedChipId === null) return;
    const timer = setTimeout(() => setExpandedChipId(null), 10_000);
    return () => clearTimeout(timer);
  }, [expandedChipId]);
  const selectedResource = useSelector(selectSelectedResource);
  const activeSessionStatus = useSelector(selectActiveSessionStatus);
  const activeSessionStatusLoading = useSelector(selectActiveSessionStatusLoading);
  const resourceChallengeStatus = useSelector(selectResourceChallengeStatus);
  const challengeSubmissions = useSelector(selectChallengeSubmissions);

  useEffect(() => {
    dispatch(fetchActiveSessionCounts());

    const interval = setInterval(() => {
      dispatch(fetchActiveSessionCounts());
    }, GATE_REFRESH_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [dispatch]);

  useEffect(() => {
    if (activeSessionStatus.sessionId && selectedResource?.id) {
      dispatch(fetchResourceChallengeStatus({
        sessionId: activeSessionStatus.sessionId,
        resourceId: selectedResource.id,
      }));
    }
  }, [dispatch, activeSessionStatus.sessionId, selectedResource?.id]);

  const hasResourceChallenge = selectedResource && resourceChallengeStatus.resourceId === selectedResource.id;
  const challenges = hasResourceChallenge ? (resourceChallengeStatus.challenges || []) : [];
  const challengeKey = challenges.map((c) => c.id).join(',');

  useEffect(() => {
    if (!activeSessionStatus.isOpen || challenges.length === 0) return;
    const challengeIds = challenges.map((c) => c.id);
    dispatch(fetchChallengeSubmissions({ challengeIds }));
    return () => dispatch(clearChallengeSubmissions());
  }, [dispatch, activeSessionStatus.isOpen, challengeKey]); // eslint-disable-line react-hooks/exhaustive-deps

  if ((!activeSessionStatusLoading && !activeSessionStatus.sessionId) || !activeSessionStatus.isOpen) {
    return null;
  }

  const gateClosureText = formatDateTimeInResourceTz(activeSessionStatus.gateClosureAt);
  const tzLabel = getResourceTimezoneLabel(undefined, new Date(activeSessionStatus.gateClosureAt || Date.now()));
  const hasForecastRange = hasResourceChallenge && challenges.length > 0;

  return (
    <div className="inline-flex max-w-full flex-col items-stretch overflow-hidden rounded-md border border-gray-200/90 bg-surface/90 text-sm text-gray-600 shadow-sm dark:border-gray-700/90 dark:bg-gray-870/90 dark:text-gray-300 sm:flex-row sm:items-center">
      <div className="min-w-0 px-4 py-2 text-center">
        <div className="leading-5 sm:whitespace-nowrap">
          Session <span className="font-bold">#{activeSessionStatus.sessionId}</span>{' '}
          <span className="font-semibold text-green-600 dark:text-green-400">open</span>
          {gateClosureText && (
            <>
              {' '}• closes <span className="font-medium">{gateClosureText} {tzLabel}</span>
            </>
          )}
        </div>
      </div>
      {hasForecastRange && (
        <div className="flex flex-wrap items-center justify-center gap-1.5 border-t border-gray-200/80 px-3 py-2 dark:border-gray-700/80 sm:border-l sm:border-t-0">
          {challenges.map(c => {
            const isExpanded = expandedChipId === c.id;
            const isSubmitted = !!challengeSubmissions[c.id]?.submitted;
            let pillClass;
            if (isExpanded) {
              pillClass = 'bg-brand-50/80 ring-1 ring-brand-400 dark:bg-brand-900/30 dark:ring-brand-500';
            } else if (isSubmitted) {
              pillClass = 'bg-green-50 hover:bg-green-100 dark:bg-green-900/25 dark:hover:bg-green-900/40';
            } else {
              pillClass = 'bg-gray-100/80 hover:bg-gray-200/80 dark:bg-gray-700/60 dark:hover:bg-gray-600/60';
            }
            return (
              <button
                key={c.id}
                type="button"
                onClick={() => toggleChip(c.id)}
                className={`inline-flex items-center rounded px-2 py-0.5 transition-all cursor-pointer focus:outline-none ${pillClass}`}
              >
                <span className="inline-flex flex-col items-center">
                  <span
                    className={`text-[11px] font-semibold leading-4 ${
                      isSubmitted && !isExpanded
                        ? 'text-green-700 dark:text-green-300'
                        : 'text-gray-700 dark:text-gray-200'
                    }`}
                  >
                    {formatChallengeBadge(c)}
                  </span>
                  {c.targetDay && (
                    <span
                      className={`text-[10px] leading-3 ${
                        isSubmitted && !isExpanded
                          ? 'text-green-600/70 dark:text-green-400/70'
                          : 'text-gray-400 dark:text-gray-500'
                      }`}
                    >
                      {formatTargetDay(c.targetDay)}
                    </span>
                  )}
                </span>
                {isExpanded && (
                  <span className="inline-flex items-center overflow-hidden transition-all duration-300 ease-in-out max-w-xs opacity-100">
                    <span className="mx-1.5 self-stretch border-l border-gray-300/60 dark:border-gray-600/60" />
                    <span className="whitespace-nowrap text-[10px] text-gray-500 dark:text-gray-400">
                      {(c.challengeTypeLabel || 'Challenge')} • {formatUtcDateTime(c.startDatetime)} → {formatUtcDateTime(c.endDatetime)} UTC
                    </span>
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
